import React, { ReactElement, useEffect, useState } from 'react'
import { connect } from 'react-redux'
import { enableCertificateForUser, fetchClientAuthCerts } from '../../client-auth/ClientAuth.actions'
import {
  createStdUser,
  deleteUser,
  fetchUserById,
  resendSsoEmail,
  resendVerificationLink,
  resetMfaLink,
  sendSetupCacBasedLoginEmail,
  updateManagerPreference
} from 'core/actions/Users.actions'
import { fetchPermissions } from 'core/actions/Profile.actions';
import { IAppState } from 'core/store/store'
import { translate } from 'shared/helpers/utils'
import { IUserDetailsStateProps, TUserDetailsProps, Sections } from './UserDetails.types'
import { UserDetailsOwnProps } from 'shared/components/user-details/UserDetails.types'
import { deleteApiToken, editApiToken, fetchApiTokens } from '../../api-tokens/ApiTokens.actions'
import { fetchTenantById } from '../../tenants/Tenants.actions'
import { withLocalize } from 'react-localize-redux'
import NoPermissionsPage from 'shared/components/no-permissions-page'
import { SecondaryNav, SecondaryNavItem, } from '@digicert/dcone-common-ui'
import { getPermissions } from '../../../core/selectors/Profile.selectors'
import { IAccessScope, IUser } from 'reducers/Reducers.interfaces'
import styles from './UserDetails.module.scss'
import { useLocation } from 'react-router-dom'
import { useHistory } from 'react-router-dom'
import { RouterConfig } from '../../../core/utils/constants'
import UserOverview from './user-overview/UserOverview'
import UserServicesView from './user-services/UserServicesView'
import { fetchServicesByUserId } from 'secure-pages/tenants/tenant/services/Services.actions';
import { initLocalization } from 'core/actions/Localization.actions';
import AccessCredentials from './access-credentials/AccessCredentials';

const UserDetails = (props: TUserDetailsProps): ReactElement => {
  const {
    user,
    fetchUserById,
    deleteUser,
    match,
    fetchPermissions,
    allPermissions,
    profile,
    fetchTenantById,
    initLocalization,
    resendVerificationLink,
    resetMfaLink,
    features
  } = props
  const [userDetails, setUserDetails] = useState(user)
  const [userAccessError, setUserAccessError] = useState(false)
  const [primaryAccount, setPrimaryAccount] = useState(undefined)
  const [accountName, setAccountName] = useState('')
  const [roles, setRoles] = useState<Array<any>>([])
  const [loading, setLoading] = useState(false)
  const { id: tenantId } = match.params
  const [systemAccount, setSystemAccount] = useState(false)
  const [section, setSection] = useState(Sections.Overview)
  const location = useLocation()
  const history = useHistory()
  const [navItems, setNavItems] = useState<SecondaryNavItem[]>([
    {
      id: 'secondary-nav-',
      label: userDetails?.first_name + ' ' + userDetails?.last_name,
      collapsed: false,
      subItems: [
        {
          id: Sections.Overview,
          label: translate('notifications.users.details.overview'),
          url: '#',
          onClick: (e) => {
            e.preventDefault()
            history.push(RouterConfig.administrators.details(match.params.id))
            setSection(Sections.Overview)
          },
          active: window.location.pathname === RouterConfig.administrators.details(match.params.id),
          externalLink: false,
          disabled: false,
        },
        {
          id: Sections.Services,
          label: translate('notifications.users.details.services'),
          url: '#',
          onClick: (e) => {
            e.preventDefault()
            history.push(RouterConfig.administrators.services(match.params.id))
            setSection(Sections.Services)
          },
          active: window.location.href.includes(Sections.Services),
          externalLink: false,
          disabled: false
        },
        {
          id: Sections.Credentials,
          label: translate('notifications.users.details.credentials'),
          url: '#',
          onClick: (e) => {
            e.preventDefault()
            history.push(RouterConfig.administrators.credentials(match.params.id))
            setSection(Sections.Credentials)
          },
          active: window.location.href.includes(Sections.Credentials),
          externalLink: false,
          disabled: false
        }
      ]
    }
  ])

  useEffect(() => {
    if (typeof userDetails != undefined) {
      fetchUserById(match.params.id).then((response: any) => {
        if (response) {
          if (response.permissions) {
            if (response.access_scope === IAccessScope.account) {
              fetchPermissions(response.access_scope.toUpperCase(), response.primary_account_id)
            } else {
              fetchPermissions(response.access_scope.toUpperCase())
            }

          }
          setUserDetails(response)
          response.access_scope === IAccessScope.system
            ? setAccountName(translate('accounts.systemAccount') as string)
            : setAccountName(response.accounts.filter(item => item.id === response.primary_account_id)[0].name)
          if (profile && profile?.permissions.filter(perm => perm.name === 'VIEW_OL_ACCOUNT').length > 0) {
            fetchTenantById(response.primary_account_id).then((res: any) => {
              setPrimaryAccount(res)
            })
          }
        }
      }).catch((error: any) => {
        if (error.message.includes('userAccessError')) {
          setUserAccessError(true)
        }
      })
    }
  }, [features])

  useEffect(() => {
    const currentPath = location.pathname.split("/").pop() || ''
    switch (currentPath) {
      case Sections.Services:
        setSection(Sections.Services)
        break;
      case Sections.Credentials:
        setSection(Sections.Credentials)
        break;
      default: setSection(Sections.Overview)
        break;
    }
    if (userDetails) {
      let tmpNavItems: Array<SecondaryNavItem> = [...navItems]
      tmpNavItems.map((item) => {
        item.label = userDetails?.first_name + ' ' + userDetails?.last_name
        if (userDetails.access_scope === IAccessScope.system) {
          item.subItems = item.subItems.filter(subItem => subItem.id !== Sections.Services)
        }
        if (userDetails.id !== profile?.id) {
          item.subItems = item.subItems.filter(subItem => subItem.id !== Sections.Credentials)
        }
        if (userDetails.access_scope === IAccessScope.system) {
          item.subItems = item.subItems.filter(subItem => subItem.id !== Sections.Services)
        }
        item.subItems.map((subItem) => {
          if (subItem.id === Sections.Overview && currentPath === userDetails.id) {
            subItem.active = true;
          }
          else {
            subItem.active = location.pathname.includes(subItem.id)
          }
        })
      })
      setNavItems(tmpNavItems)
    }
  }, [location, userDetails]);

  useEffect(() => {
    if (userDetails) {
      if (typeof allPermissions !== undefined) {
        setLoading(true)
      }
    }
  }, [userDetails, allPermissions])

  const updateUser = () => {
    fetchUserById(match.params.id).then((response: IUser) => {
      if (response) {
        setUserDetails(response)
      }
    })
  }

  if (userAccessError) {
    return (
      <NoPermissionsPage />
    )
  }

  const loadView = (section: string, user: IUser) => {
    switch (section) {
      case Sections.Services:
        return <UserServicesView user={user} profile={profile} fetchServicesByUserId={fetchServicesByUserId} />
      case Sections.Credentials:
        return <AccessCredentials user={user} profile={profile} />
      default:
        return <UserOverview user={user} deleteUser={deleteUser} profile={profile} updateDetails={updateUser} initLocalization={initLocalization} resetMfaLink={resetMfaLink} resendVerificationLink={resendVerificationLink}/>
    }
  }

  return (
    <div id='user_detail_page' className={styles.userDetails}>
      {userDetails && (
        <SecondaryNav className={styles.secondaryNav} items={navItems} />
      )}

      <div className={styles.content}>
        {userDetails && (
          loadView(section, userDetails)
        )}
      </div>
    </div>
  )
}

UserDetails.displayName = 'UserDetails'

export default connect(
  (
    state: IAppState,
    ownProps: UserDetailsOwnProps
  ): IUserDetailsStateProps => {
    return {
      user: state.users.items.find(
        (user) => user.id === ownProps.match.params.id
      ),
      profile: state.profile.data,
      allPermissions: state.profile.permissions,
      client_auth: state.clientAuthCerts.items,
      tokens: state.apiTokens.items,
      localeList: state.locales,
      profileId: state.profile.data?.id,
      userProfile: state.userProfile.data,
      features: state.features,
      permissions: getPermissions(state)
    }
  },
  {
    fetchUserById,
    deleteUser,
    fetchPermissions,
    fetchClientAuthCerts,
    enableCertificateForUser,
    fetchApiTokens,
    initLocalization,
    editApiToken,
    deleteApiToken,
    fetchTenantById,
    resendVerificationLink,
    resetMfaLink,
    createStdUser,
    resendSsoEmail,
    sendSetupCacBasedLoginEmail,
    updateManagerPreference,
    fetchServicesByUserId
  }
)(withLocalize(UserDetails))
